import { createReducer } from "deox";

import { appConfigError, appConfigLoading, appConfigSuccess } from "./appConfigsStateActions";
import { IAppConfig } from "../../../types/types";
import { ccDefaultSurcharge } from "../../../utils/constants";
import { purge } from "../purge/purgeStateActions";

let _defaultSurcharge = ccDefaultSurcharge;

const defaultSoeButtonLabel = "Statement of Entitlement";
const defaultSoeModalTitle = "Generate a Statement of Entitlement";
const defaultSoeModalDescription = "Select a date range below to generate a statement of entitlement for your account.";

export type ChatbotHeaderMode = "title" | "logo";
export type ChatbotHeaderAlignment = "left" | "center" | "right";

export interface ChatbotHeaderConfig {
  mode: ChatbotHeaderMode;
  title: string;
  backgroundColor: string;
  alignment: ChatbotHeaderAlignment;
  height: number;
}

const defaultChatbotHeader: ChatbotHeaderConfig = {
  mode: "title",
  title: "Camp Australia Chat",
  backgroundColor: "#1D4E5C",
  alignment: "center",
  height: 60,
};

export interface AppConfigState {
  payload: IAppConfig[];
  isError: boolean;
  isLoading: boolean;
  isLoaded: boolean;
  creditCardSurcharge: number | null;
  rollOverShowAddChild: boolean | null;
  enableChatbot: boolean;
  chatbotHeader: ChatbotHeaderConfig;
  chatbotDirectlineTokenUrl: string;
  isMaguireTransitionState: boolean;
  isNewSystemFlow: boolean;
  isMaguireTransitionDay2: boolean;
  weeklyBillingCycleCutOffId: number;
  statementOfEntitlementEnabled: boolean;
  soeButtonLabel: string;
  soeModalTitle: string;
  soeModalDescription: string;
  siteBannerEnabled: boolean;
  siteBannerHtml: string;
}

export const initialState: AppConfigState = {
  payload: [],
  isError: false,
  isLoading: false,
  isLoaded: false,
  creditCardSurcharge: _defaultSurcharge,
  rollOverShowAddChild: false,
  enableChatbot: false,
  chatbotHeader: defaultChatbotHeader,
  chatbotDirectlineTokenUrl: "",
  isMaguireTransitionState: false,
  isNewSystemFlow: false,
  isMaguireTransitionDay2: false,
  weeklyBillingCycleCutOffId: 0,
  statementOfEntitlementEnabled: false,
  soeButtonLabel: defaultSoeButtonLabel,
  soeModalTitle: defaultSoeModalTitle,
  soeModalDescription: defaultSoeModalDescription,
  siteBannerEnabled: false,
  siteBannerHtml: "",
};

function getcreditCardSurcharge(payload: IAppConfig[]) {
  const config = payload.find((x) => x.name === "creditCardSurcharge");
  if (config && config.value) {
    return parseFloat(config.value);
  }
  return _defaultSurcharge;
}

function getRollOverShowAddChild(payload: IAppConfig[]): boolean {
  return payload.find((x) => x.name === "rollOverShowAddChild")?.value?.toLowerCase() === "true";
}

function getEnableChatbot(payload: IAppConfig[]): boolean {
  return payload.find((x) => x.name === "enableChatbot")?.value?.toLowerCase() === "true";
}

function getChatbotHeader(payload: IAppConfig[]): ChatbotHeaderConfig {
  const raw = payload.find((x) => x.name === "chatbotTitle")?.value;
  if (raw) {
    try {
      return { ...defaultChatbotHeader, ...JSON.parse(raw) };
    } catch {
      // fall through to default
    }
  }
  return defaultChatbotHeader;
}

function getChatbotDirectlineTokenUrl(payload: IAppConfig[]): string {
  return payload.find((x) => x.name === "chatbot.directlineTokenUrl")?.value || "";
}

function getIsMaguireTransitionState(payload: IAppConfig[]): boolean {
  return payload.find((x) => x.name === "isMaguireTransitionState")?.value?.toLowerCase() === "true";
}

function getIsNewSystemFlow(payload: IAppConfig[]): boolean {
  return payload.find((x) => x.name === "isNewSystemFlow")?.value?.toLowerCase() === "true";
}

function getIsMaguireTransitionDay2(payload: IAppConfig[]): boolean {
  return payload.find((x) => x.name === "isMaguireTransitionDay2")?.value?.toLowerCase() === "true";
}

function getWeeklyBillingCycleCutOffId(payload: IAppConfig[]): number {
  const value = payload.find((x) => x.name === "weeklyBillingCycleCutOffId")?.value;
  return value ? parseInt(value, 10) : 0;
}

// Catalyst-backed flag (no DB AppConfig fallback) gating the Billing page's Statement of
// Entitlement download button. Defaults to disabled if the flag is absent (e.g. Catalyst unavailable).
function getStatementOfEntitlementEnabled(payload: IAppConfig[]): boolean {
  return payload.find((x) => x.name === "billing.soe.downloadEnabled")?.value?.toLowerCase() === "true";
}

// Catalyst-backed config for the SOE button's label text.
function getSoeButtonLabel(payload: IAppConfig[]): string {
  return payload.find((x) => x.name === "soeButtonLabel")?.value || defaultSoeButtonLabel;
}

// Catalyst-backed config for the SOE modal's title text.
function getSoeModalTitle(payload: IAppConfig[]): string {
  return payload.find((x) => x.name === "soe.modal.title")?.value || defaultSoeModalTitle;
}

// Catalyst-backed config for the SOE modal's description text.
function getSoeModalDescription(payload: IAppConfig[]): string {
  return payload.find((x) => x.name === "soe.modal.description")?.value || defaultSoeModalDescription;
}

// Catalyst-backed Feature flag (no DB AppConfig fallback) gating the site-wide banner.
function getSiteBannerEnabled(payload: IAppConfig[]): boolean {
  return payload.find((x) => x.name === "banner.enabled")?.value?.toLowerCase() === "true";
}

// Catalyst-backed config holding the banner's raw HTML/CSS content.
function getSiteBannerHtml(payload: IAppConfig[]): string {
  return payload.find((x) => x.name === "banner.html")?.value || "";
}

const appConfigsReducer = createReducer(initialState, (handleAction) => [
  handleAction(
    appConfigSuccess,
    (state, action): AppConfigState => ({
      ...state,
      isLoading: false,
      isError: false,
      isLoaded: true,
      payload: action.payload,
      creditCardSurcharge: getcreditCardSurcharge(action.payload),
      rollOverShowAddChild: getRollOverShowAddChild(action.payload),
      enableChatbot: getEnableChatbot(action.payload),
      chatbotHeader: getChatbotHeader(action.payload),
      chatbotDirectlineTokenUrl: getChatbotDirectlineTokenUrl(action.payload),
      isMaguireTransitionState: getIsMaguireTransitionState(action.payload),
      isNewSystemFlow: getIsNewSystemFlow(action.payload),
      isMaguireTransitionDay2: getIsMaguireTransitionDay2(action.payload),
      weeklyBillingCycleCutOffId: getWeeklyBillingCycleCutOffId(action.payload),
      statementOfEntitlementEnabled: getStatementOfEntitlementEnabled(action.payload),
      soeButtonLabel: getSoeButtonLabel(action.payload),
      soeModalTitle: getSoeModalTitle(action.payload),
      soeModalDescription: getSoeModalDescription(action.payload),
      siteBannerEnabled: getSiteBannerEnabled(action.payload),
      siteBannerHtml: getSiteBannerHtml(action.payload),
    })
  ),
  handleAction(
    appConfigLoading,
    (state): AppConfigState => ({
      ...state,
      isLoading: true,
      isError: false,
      isLoaded: false,
      creditCardSurcharge: null,
      rollOverShowAddChild: null,
      enableChatbot: false,
      chatbotHeader: defaultChatbotHeader,
      chatbotDirectlineTokenUrl: "",
      isMaguireTransitionState: false,
      isNewSystemFlow: false,
      isMaguireTransitionDay2: false,
      weeklyBillingCycleCutOffId: 0,
      statementOfEntitlementEnabled: false,
      soeButtonLabel: defaultSoeButtonLabel,
      soeModalTitle: defaultSoeModalTitle,
      soeModalDescription: defaultSoeModalDescription,
      siteBannerEnabled: false,
      siteBannerHtml: "",
    })
  ),
  handleAction(
    appConfigError,
    (state): AppConfigState => ({
      ...state,
      isError: true,
      isLoading: false,
      isLoaded: false,
      payload: [],
      creditCardSurcharge: null,
      rollOverShowAddChild: null,
      enableChatbot: false,
      chatbotHeader: defaultChatbotHeader,
      chatbotDirectlineTokenUrl: "",
      isMaguireTransitionState: false,
      isNewSystemFlow: false,
      isMaguireTransitionDay2: false,
      weeklyBillingCycleCutOffId: 0,
      statementOfEntitlementEnabled: false,
      soeButtonLabel: defaultSoeButtonLabel,
      soeModalTitle: defaultSoeModalTitle,
      soeModalDescription: defaultSoeModalDescription,
      siteBannerEnabled: false,
      siteBannerHtml: "",
    })
  ),
  handleAction(purge, () => initialState),
]);

export default appConfigsReducer;
